import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, HardHat, ShieldCheck, MapPin, Globe, Send, Search } from "lucide-react";
import { toast } from "sonner";

export default function Contractors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState(null);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(null); // contractor being quoted
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/public/contractors").then(({ data }) => setContractors(data)).catch(() => setContractors([]));
  }, []);

  const filtered = useMemo(() => {
    if (!contractors) return [];
    const s = search.trim().toLowerCase();
    if (!s) return contractors;
    return contractors.filter((c) =>
      [c.company, c.country, c.city, ...(c.trades || [])].join(" ").toLowerCase().includes(s)
    );
  }, [contractors, search]);

  const openRequest = async (c) => {
    if (!user) {
      toast.info("Sign in to request a quote.");
      return navigate("/login");
    }
    setTarget(c);
    setMessage("");
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
      setProjectId(data[0]?.project_id || "");
    } catch { setProjects([]); }
  };

  const sendRequest = async () => {
    if (!projectId) return toast.error("Pick a project to share.");
    setSending(true);
    try {
      await api.post(`/contractors/${target.contractor_id}/request-quote`, { project_id: projectId, message });
      toast.success(`Quote request sent to ${target.company}. Track it on your project page.`);
      setTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary"><HardHat className="mr-2 inline h-3.5 w-3.5" />Contractor Directory</p>
        <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Vetted pros, ready to quote</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">
          Every contractor here holds a paid Costobuild membership and has been reviewed and approved by our team.
          Request a quote and their price lands right next to your AI estimate.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contractor-search"
              placeholder="Search company, trade or location…" className="rounded-sm border-border bg-transparent pl-9" />
          </div>
          <Link to="/contractors/apply" data-testid="contractor-apply-link">
            <Button variant="outline" className="rounded-sm border-primary/60 font-mono2 text-xs uppercase tracking-widest text-primary hover:bg-primary/10">
              Are you a contractor? Get listed
            </Button>
          </Link>
        </div>

        {contractors === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 border border-border p-12 text-center" data-testid="contractors-empty">
            <HardHat className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-4 font-heading text-lg font-semibold">No contractors listed yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Be the first — apply and get vetted by the Costobuild team.</p>
          </div>
        ) : (
          <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" data-testid="contractors-grid">
            {filtered.map((c) => (
              <div key={c.contractor_id} className="flex flex-col bg-background p-6" data-testid={`contractor-card-${c.contractor_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading text-lg font-bold">{c.company}</h3>
                  {c.vetted && (
                    <span className="flex shrink-0 items-center gap-1 border border-primary/50 bg-primary/10 px-1.5 py-0.5 font-mono2 text-[9px] uppercase tracking-widest text-primary">
                      <ShieldCheck className="h-3 w-3" /> Vetted
                    </span>
                  )}
                </div>
                {(c.city || c.country) && (
                  <p className="mt-1 flex items-center gap-1 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {[c.city, c.country].filter(Boolean).join(", ")}
                  </p>
                )}
                {c.trades?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.trades.map((t) => (
                      <span key={t} className="border border-border px-1.5 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{t}</span>
                    ))}
                  </div>
                )}
                {c.bio && <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{c.bio}</p>}
                <div className="mt-auto flex items-center justify-between gap-2 pt-5">
                  {c.years_experience != null && (
                    <span className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{c.years_experience}+ yrs exp.</span>
                  )}
                  {c.website && (
                    <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
                      <Globe className="h-3 w-3" /> Site
                    </a>
                  )}
                  <Button size="sm" onClick={() => openRequest(c)} data-testid={`request-quote-${c.contractor_id}`}
                    className="ml-auto rounded-sm bg-primary font-mono2 text-[10px] uppercase tracking-widest text-black hover:bg-primary/90">
                    Request Quote
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="border-border">
          <DialogHeader><DialogTitle className="font-heading">Request a quote from {target?.company}</DialogTitle></DialogHeader>
          {projects.length === 0 ? (
            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">You need a project with an estimate first.</p>
              <Button onClick={() => navigate("/builder")} className="mt-4 rounded-sm bg-primary font-mono2 text-xs uppercase tracking-widest text-black hover:bg-primary/90">
                Create a project
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Project</label>
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger data-testid="request-quote-project-select" className="mt-1.5 rounded-sm border-border bg-transparent"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Message (optional)</label>
                  <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="request-quote-message"
                    placeholder="Timeline, budget, anything the contractor should know…" className="mt-1.5 rounded-sm border-border bg-transparent" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={sendRequest} disabled={sending} data-testid="request-quote-send"
                  className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send Request
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
